import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { classicalLessons, trackAudioUrl } from '../../apps/Classical/catalog';
import { loadStationTracks, stations } from '../../apps/Radio/catalog';
import { trackEvent } from '../../analytics';
import { AudioQueueTrack, useAudioPlayer } from '../AudioPlayer/AudioPlayerContext';
import './RadioApplet.css';

const RadioApplet: React.FC = () => {
  const player = useAudioPlayer();
  const [isOpen, setIsOpen] = useState(false);
  const [stationId, setStationId] = useState(stations[0].id);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const station = useMemo(
    () => stations.find((candidate) => candidate.id === stationId) || stations[0],
    [stationId],
  );

  useEffect(() => {
    if (player.queue?.kind === 'radio' && player.queue.stationId) {
      setStationId(player.queue.stationId);
    }
  }, [player.queue]);

  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isOpen]);

  const tuneStation = async (nextStationId: string, autoplay: boolean) => {
    const nextStation = stations.find((candidate) => candidate.id === nextStationId) || stations[0];
    setStationId(nextStation.id);
    setIsLoading(true);
    setLoadError(null);
    try {
      const loaded = await loadStationTracks(nextStation);
      const tracks: AudioQueueTrack[] = loaded.map((item) => ({
        id: `${nextStation.id}:${item.filename}`,
        title: item.title,
        creator: item.creator,
        url: item.url,
        archiveId: item.archiveId,
        licenseUrl: item.licenseUrl,
        sourceTitle: item.sourceTitle,
      }));
      if (!tracks.length) {
        setLoadError('No tracks on this frequency.');
        return;
      }
      player.loadQueue(
        { key: `radio:${nextStation.id}`, kind: 'radio', title: nextStation.name, stationId: nextStation.id },
        tracks,
        { autoplay },
      );
    } catch {
      setLoadError('Archive signal unavailable.');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePlayback = async () => {
    if (!player.track) {
      await tuneStation(station.id, true);
      return;
    }
    await player.togglePlayback();
  };

  const selectStation = (nextStationId: string) => {
    const nextStation = stations.find((candidate) => candidate.id === nextStationId);
    if (nextStation) {
      trackEvent('Radio Station Selected', {
        station_id: nextStation.id,
        station_name: nextStation.name,
        source: 'mini_player',
      });
    }
    tuneStation(nextStationId, player.isPlaying);
  };

  const tuneClassicalLesson = (lessonId: string) => {
    const lesson = classicalLessons.find((candidate) => candidate.id === lessonId);
    if (!lesson) return;

    const tracks: AudioQueueTrack[] = lesson.tracks.map((item) => ({
      id: item.id,
      title: item.movement ? `${item.work} — ${item.movement}` : item.work,
      creator: `${item.composer} · ${item.performers}`,
      url: trackAudioUrl(item),
      archiveId: item.archiveIdentifier,
      licenseUrl: item.licenseUrl,
      sourceTitle: item.work,
    }));
    trackEvent('Classical Lesson Selected', {
      lesson_id: lesson.id,
      lesson_number: lesson.number,
      source: 'mini_player',
    });
    player.loadQueue(
      { key: `classical:${lesson.id}`, kind: 'classical', title: `Classical — Lesson ${lesson.number}` },
      tracks,
      { autoplay: player.isPlaying },
    );
  };

  const selectSource = (value: string) => {
    if (value.startsWith('classical:')) {
      tuneClassicalLesson(value.slice('classical:'.length));
      return;
    }
    selectStation(value);
  };

  const isClassical = player.queue?.kind === 'classical';
  const selectedSource = isClassical ? player.queue?.key || station.id : station.id;
  const activeClassicalLesson = isClassical ? player.queue?.key.replace('classical:', '') : '';
  const sourceLink = isClassical
    ? `/classical?lesson=${encodeURIComponent(activeClassicalLesson || '')}&track=${encodeURIComponent(player.track?.id || '')}`
    : `/radio?station=${player.queue?.stationId || station.id}`;

  return (
    <aside className={`radio-applet${isOpen ? ' is-open' : ''}${player.isPlaying ? ' is-playing' : ''}`}>
      {isOpen && (
        <section className="radio-applet__panel" aria-label="Lrner audio player">
          <header>
            <div><strong>{isClassical ? 'CLASSICAL' : 'LRNER RADIO'}</strong><span>{player.queue?.title || (station.presentation === 'album' ? 'Album' : `${station.frequency} FM`)}</span></div>
            <button type="button" onClick={() => setIsOpen(false)} aria-label="Close audio player">×</button>
          </header>

          <label className="radio-applet__station-select">
            <span>Station</span>
            <select value={selectedSource} onChange={(event) => selectSource(event.target.value)}>
              <optgroup label="Albums">
                {stations.filter((candidate) => candidate.presentation === 'album').map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.artist} — {candidate.name}</option>
                ))}
              </optgroup>
              <optgroup label="Vintage Radio">
                {stations.filter((candidate) => candidate.presentation !== 'album').map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.frequency} FM — {candidate.name}</option>
                ))}
              </optgroup>
              <optgroup label="Classical Lessons">
                {classicalLessons.map((lesson) => (
                  <option key={lesson.id} value={`classical:${lesson.id}`}>Classical — Lesson {lesson.number}</option>
                ))}
              </optgroup>
            </select>
          </label>

          <div className="radio-applet__track" aria-live="polite">
            <span>{isLoading ? 'Tuning…' : player.isPlaying ? 'Now playing' : player.queue?.title || station.name}</span>
            <strong>{player.track?.title || 'Audio pending'}</strong>
            <small>{player.track?.creator || '—'}</small>
          </div>

          <div className="radio-applet__controls">
            <button type="button" onClick={() => player.moveTrack(-1)} disabled={!player.track} aria-label="Previous track">←</button>
            <button type="button" className="radio-applet__play" onClick={togglePlayback} disabled={isLoading}>
              {player.isPlaying ? 'Pause' : 'Play'}
            </button>
            <button type="button" onClick={() => player.moveTrack(1)} disabled={!player.track} aria-label="Next track">→</button>
          </div>

          {(loadError || player.error) && <p className="radio-applet__error" role="alert">{loadError || player.error}</p>}

          <footer>
            {player.track?.archiveId && <a href={`https://archive.org/details/${player.track.archiveId}`} target="_blank" rel="noreferrer">Source ↗</a>}
            <Link to={sourceLink} onClick={() => setIsOpen(false)}>Open full player →</Link>
          </footer>
        </section>
      )}

      <button
        type="button"
        className="radio-applet__toggle"
        onClick={() => setIsOpen((current) => !current)}
        aria-label={isOpen ? 'Close Lrner audio player' : 'Open Lrner audio player'}
        aria-expanded={isOpen}
      >
        <span aria-hidden="true">♫</span>
      </button>
    </aside>
  );
};

export default RadioApplet;
